/* eslint-disable no-nested-ternary */

import { type Row } from '@tanstack/react-table';

export const basic = <TData extends Record<string, any> = object>(
  rowA: Row<TData>,
  rowB: Row<TData>,
  columnId: string,
) => {
  const cell = rowA.getAllCells().find(c => c.id === `${rowA.id}_${columnId}`);
  const sortDir = cell?.column.getIsSorted();

  if (!sortDir) {
    return 0;
  }

  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);

  if (isNullable(a) || isNullable(b)) {
    return compareNullables(a, b, sortDir, cell?.column.columnDef.invertSorting || false);
  }
  return compareBasic(a, b);
};

export const compareDiffs = <TData extends Record<string, any> = object>(
  rowA: Row<TData>,
  rowB: Row<TData>,
  columnId: string,
) => {
  const cell = rowA.getAllCells().find(c => c.id === `${rowA.id}_${columnId}`);
  const sortDir = cell?.column.getIsSorted();

  if (!sortDir) {
    return 0;
  }

  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);

  if (isZero(a) || isZero(b) || isNullable(a) || isNullable(b)) {
    return compareNullables(a, b, sortDir, cell?.column.columnDef.invertSorting || false);
  }
  return compareBasic(a, b);
};

function compareNullables(
  rowA: unknown,
  rowB: unknown,
  sortDir: 'asc' | 'desc',
  invertSorting: boolean,
) {
  const weightRowA = getWeight(rowA, sortDir, invertSorting);
  const weightRowB = getWeight(rowB, sortDir, invertSorting);
  return compareBasic(weightRowA, weightRowB);
}

function getWeight(value: unknown, sortDir: 'asc' | 'desc', invertSorting: boolean): number {
  const base = sortDir === 'asc' ? -1 : 1;
  if (isNullable(value)) {
    return invertSorting ? base * 2 : base * 0;
  }
  if (value === 0) {
    return base * 1;
  }
  return invertSorting ? base * 0 : base * 2;
}

function isNullable(value: unknown): boolean {
  return value === null || typeof value === 'undefined';
}
function isZero(value: unknown): boolean {
  return value === 0;
}

function compareBasic(a: any, b: any) {
  return a === b ? 0 : a > b ? 1 : -1;
}
